// Baseball live-state visuals shared by the board card (compact badge) and the
// game detail page (full at-bat panel). Data comes from `game.live` built in
// lib/sharpapi.js buildBaseballLive(): { inningLabel, inningHalf, inning,
// balls, strikes, outs, batter, pitcher, bases:{first,second,third},
// runnersOn, battingSide }.

import { useTheme } from '../../contexts/ThemeContext';

const FILLED = '#facc15';
const EMPTY_STROKE = '#4b5563';

// Small diamond of the three bases (home is implied at the bottom). Occupied
// bags fill amber; empty bags are hollow.
export function BasesDiamond({ bases = {}, size = 20, isLight = false }) {
  const s = size;
  const first = bases.first === true;
  const second = bases.second === true;
  const third = bases.third === true;
  const emptyStroke = isLight ? '#cbd5e1' : EMPTY_STROKE;
  const bag = (cx, cy, on) => {
    const w = s * 0.24;
    return (
      <rect
        x={cx - w / 2}
        y={cy - w / 2}
        width={w}
        height={w}
        transform={`rotate(45 ${cx} ${cy})`}
        fill={on ? FILLED : 'none'}
        stroke={on ? FILLED : emptyStroke}
        strokeWidth={s * 0.06}
        rx={s * 0.02}
      />
    );
  };
  return (
    <svg width={s} height={s * 0.72} viewBox={`0 0 ${s} ${s * 0.72}`} aria-hidden="true">
      {bag(s * 0.5, s * 0.24, second)}
      {bag(s * 0.78, s * 0.46, first)}
      {bag(s * 0.22, s * 0.46, third)}
    </svg>
  );
}

// Filled/empty pips for a ball/strike/out count.
function CountPips({ count = 0, total, color = '#ffffff', isLight = false }) {
  const emptyStroke = isLight ? '#cbd5e1' : EMPTY_STROKE;
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className="rounded-full"
          style={{
            width: 6,
            height: 6,
            backgroundColor: i < count ? color : 'transparent',
            border: `1px solid ${i < count ? color : emptyStroke}`,
          }}
        />
      ))}
    </span>
  );
}

const inningArrow = (half) => (half === 'bottom' ? '▼' : half === 'middle' ? '■' : '▲');

// Compact badge for the board card: inning + tiny bases + outs.
export function BaseballLiveBadge({ live }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  if (!live) return null;
  const { inningLabel, inningHalf, bases, outs } = live;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
      {inningLabel && (
        <span className="inline-flex items-center gap-0.5 tabular-nums">
          <span style={{ color: FILLED, fontSize: 8, lineHeight: 1 }}>{inningArrow(inningHalf)}</span>
          {inningLabel}
        </span>
      )}
      {bases && (bases.first || bases.second || bases.third) ? (
        <BasesDiamond bases={bases} size={16} isLight={isLight} />
      ) : null}
      {Number.isFinite(outs) && (
        <span className="tabular-nums text-gray-500">{outs} {outs === 1 ? 'out' : 'outs'}</span>
      )}
    </span>
  );
}

// Full at-bat panel for the detail page: matchup, count, outs, bases.
export function BaseballLivePanel({ live, accent = '#facc15' }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  if (!live) return null;
  const { inningLabel, inningHalf, balls, strikes, outs, batter, pitcher, bases, battingSide } = live;
  const hasCount = Number.isFinite(balls) || Number.isFinite(strikes);
  // Theme-aware surface: hardcoded dark hex won't flip under html.light, so the
  // panel used to render as a black card on the light board (invisible content).
  const panelBg = isLight ? '#ffffff' : '#0d0d0d';
  const panelBorder = isLight ? '#e5e7eb' : '#1a1a1a';
  const labelColor = 'text-gray-500';
  const inningColor = isLight ? 'text-gray-700' : 'text-gray-200';
  const batterColor = isLight ? 'text-gray-900' : 'text-white';
  const pitcherColor = isLight ? 'text-gray-600' : 'text-gray-300';
  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: panelBg, border: `1px solid ${panelBorder}` }}>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-[10px] font-black uppercase tracking-widest ${labelColor}`}>Live At-Bat</span>
        {inningLabel && (
          <span className={`inline-flex items-center gap-1 text-xs font-bold tabular-nums ${inningColor}`}>
            <span style={{ color: accent, fontSize: 9 }}>{inningArrow(inningHalf)}</span>
            {inningLabel}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="flex flex-col items-center gap-1">
          <BasesDiamond bases={bases} size={52} isLight={isLight} />
          {Number.isFinite(outs) && (
            <div className="flex items-center gap-1.5">
              <span className={`text-[9px] font-bold uppercase tracking-wider ${labelColor}`}>Out</span>
              <CountPips count={outs} total={2} color="#f87171" isLight={isLight} />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {hasCount && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-bold uppercase tracking-wider w-10 ${labelColor}`}>Balls</span>
                <CountPips count={balls || 0} total={3} color="#34d399" isLight={isLight} />
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] font-bold uppercase tracking-wider w-12 ${labelColor}`}>Strikes</span>
                <CountPips count={strikes || 0} total={2} color={accent} isLight={isLight} />
              </div>
            </div>
          )}
          {batter && (
            <div className="min-w-0">
              <div className={`text-[9px] font-bold uppercase tracking-wider ${labelColor}`}>At Bat</div>
              <div className={`text-sm font-bold truncate ${batterColor}`}>{batter}</div>
            </div>
          )}
          {pitcher && (
            <div className="min-w-0">
              <div className={`text-[9px] font-bold uppercase tracking-wider ${labelColor}`}>Pitching</div>
              <div className={`text-sm font-semibold truncate ${pitcherColor}`}>{pitcher}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
